import { SearchPlacementRequest } from './../_models/placement-search/search-placement-request.model';
import { PlacementService } from './../_services/placementservice';
import { Component, Input, OnDestroy, OnInit, Optional, TemplateRef, ViewChild } from '@angular/core';
import { ThemeService } from '../_helpers/theming-engine/theme.service';
import { SearchPlacementResult } from '../_models/placement-search/search-placement-result.model';
import { SuccessModalHelperService } from '../_helpers/success-modal-helper/success-modal-helper.service';
import { TutorReportsHelperService } from '../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureHelperService } from '../_helpers/feature-helper/feature-helper.service';
import { PlacementSearchDataHelperService } from '../_helpers/placement-search-data-helper/placement-search-data-helper.service';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { AuthenticationStateHelperService } from '../_helpers/authentication-state-helper/authentication-state-helper.service';
import { MatDialog } from '@angular/material/dialog';
import { StudentGroupMaintenanceComponent } from '../maintenance/student-group-maintenance/student-group-maintenance.component';
import { ClientService } from '../_services/client.service';
import { PlacementMaintenance } from '../_models/maintenance/placement-maintenance.model';
import { ClientUserDetails } from '../_models/maintenance/client-user-details.model';
import { ColumnDef, DEFAULT_PAGE_SIZE, PageChange, SortChange, SortDirection, StatusVariant } from '../shared/data-table/data-table.model';
import { PagedRequest, PagedResponse } from '../_models/common/paged-response.model';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { FeatureSwitchService } from '../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../_constants/feature-switch-keys';
import { DELIVERY_MODE_OPTIONS, ORDER_TYPE_OPTIONS } from '../_constants/search-filter-options';
import { FilterSpec, FilterState } from '../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../shared/filter-ux/screen-filter-ux';
import { DatePreset, isDatePreset, resolveDatePreset, toIsoDate } from '../shared/filter-ux/date-presets';
import { DatePresetSelection } from '../shared/filter-ux/date-presets.component';
import { ClientSuggestionsService } from '../shared/typeahead/client-suggestions.service';

@Component({
    selector: 'placement-search',
    templateUrl: './placement-search.component.html',
    styleUrls: ['./placement-search.component.css'],
    standalone: false
})
export class PlacementSearchComponent implements OnInit, OnDestroy {
  @Input() externalView: boolean = false;

  @ViewChild('utilityBarForm', { static: true }) utilityBarForm: TemplateRef<any>;

  public columns: ColumnDef<SearchPlacementResult>[] = [];

  public dotsFound: boolean = false;
  public bookingSetupViewPermission: boolean = false;
  public tutorReportsViewPermission: boolean = false;
  public searchTextPlaceholder: string = "";
  public searchTextTooltip: string = "";
  public bookingIdentifierPlaceholder: string = "";
  public bookingIdentifierTooltip: string = "";

  /**
   * Server-side paging state (Plans/table-lazy-loading.md §4 A1). This screen always opts in to
   * paging - `totalCount` drives the pager's "of Z" footer and must never be derived from
   * `items.length`, which is only ever one page's worth.
   */
  public totalCount: number = 0;
  public pageNumber: number = 1;
  public pageSize: number = DEFAULT_PAGE_SIZE;
  public sortField: string = null;
  public sortDir: SortDirection = 'asc';
  public loading: boolean = false;

  /** For the template's `*appFeatureSwitch`. */
  readonly featureKeys = FeatureSwitchKeys;
  readonly deliveryModeOptions = DELIVERY_MODE_OPTIONS;
  readonly orderTypeOptions = ORDER_TYPE_OPTIONS;
  /**
   * The filters.booking-search switch. Off (the default, and on any failure): the Brand, Delivery,
   * Order type and Start date filters don't show and the search sends none of their parameters.
   */
  public filtersEnabled: boolean = false;
  /** The date preset behind the start-date range, or null when it was set by hand. */
  public datePreset: DatePreset | null = null;
  /** search.autocomplete.booking-search: the Client Name / ID box suggests clients. Off, it is a plain box. */
  public autocompleteEnabled: boolean = false;

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
   * only: free text (client name, booking identifier, search text) stays out of the URL (browser
   * history, telemetry) and browser storage.
   */
  private static readonly filterSpec: FilterSpec = {
    brand: 'number[]', delivery: 'string', orderType: 'string', from: 'string', to: 'string', preset: 'string',
    ended: 'boolean'
  };
  /** Remembered filters and filter state in the URL; the internal screen only, while the switch is on. */
  private filterUx: ScreenFilterUx;

  /** Search requests are funnelled through this subject and switchMap'd so a rapid page/sort click
   * cannot have an earlier, slower response land after a later one (last-write-wins). */
  private readonly searchRequested = new Subject<void>();
  private readonly destroyed = new Subject<void>();

  constructor(
    public themeService: ThemeService,
    private placementService: PlacementService,
    private successModalHelper: SuccessModalHelperService,
    private tutorReportsHelperService: TutorReportsHelperService,
    private router: Router,
    private featureHelperService: FeatureHelperService,
    public searchDataHelper: PlacementSearchDataHelperService,
    private errorModalHelper: ErrorModalHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService,
    private dialog: MatDialog,
    private clientService: ClientService,
    private featureSwitches: FeatureSwitchService,
    @Optional() private route: ActivatedRoute = null,
    @Optional() public clientSuggestions: ClientSuggestionsService = null
    ) { }

  ngOnInit() {

    let featureList = new Array<string>();
    featureList.push(
      "BookingSearchView",
      "BookingSetupView",
      "TutorReportsView");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    if (!currentUserFeatures.filter(f => f.feature === "BookingSearchView")[0].enabled) {
      console.error("User does not have the required permissions to view the booking search screen");
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname }});
      this.authenticationStateHelper.unauthorizedToken = true;
    }

    this.bookingSetupViewPermission = currentUserFeatures.find(f => f.feature == "BookingSetupView").enabled;
    this.tutorReportsViewPermission = currentUserFeatures.find(f => f.feature == "TutorReportsView").enabled;

    this.searchRequested.pipe(
      switchMap(() => {
        this.loading = true;
        return this.placementService.searchPlacements(this.buildRequest(), this.buildPaging());
      }),
      takeUntil(this.destroyed)
    ).subscribe({
      next: (response: PagedResponse<SearchPlacementResult>) => {
        this.loading = false;
        this.assignData(response);
      },
      error: error => {
        this.loading = false;
        console.error(error);
        this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
      }
    });

    // The first search waits for the switch, so it cannot go out without filters the screen shows.
    this.filterUx = new ScreenFilterUx(FeatureSwitchKeys.filtersBookingSearch, PlacementSearchComponent.filterSpec, this.router, this.route);
    this.featureSwitches.isEnabled(FeatureSwitchKeys.searchAutocompleteBookingSearch).pipe(takeUntil(this.destroyed))
      .subscribe(enabled => this.autocompleteEnabled = enabled);
    this.featureSwitches.isEnabled(FeatureSwitchKeys.filtersBookingSearch).pipe(takeUntil(this.destroyed)).subscribe(enabled => {
      this.filtersEnabled = enabled;
      if (!this.externalView) {
        const cached = this.searchDataHelper.placements && this.searchDataHelper.placements.length > 0;
        // A shared link's filters win. Otherwise the filters still held from earlier in this session
        // stand, and failing that, this user's remembered ones apply.
        const initial = enabled && this.usesFilterUx()
          ? (this.filterUx.urlState() ?? (cached ? null : this.filterUx.rememberedState()))
          : null;
        if (initial) {
          this.applyFilterState(initial);
          this.searchPlacements();
        } else if (!cached) {
          this.searchPlacements();
        }
      }
    });

    this.buildColumns();

    setTimeout(() => {
      this.searchTextPlaceholder = "Search Text - eg. Session / Subject / Group / Learner";
      this.searchTextTooltip = "Please enter a session, subject, group or learner";
      this.bookingIdentifierPlaceholder =  this.themeService.siteContent.placementHeaderLabel + " Identifier";
      this.bookingIdentifierTooltip = "Please enter a  " + this.themeService.siteContent.placementHeaderLabel + " Id";
    }, 500);
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  private buildColumns(): void {
    this.columns = [
      { field: 'bookingId', header: 'Booking' },
      { field: 'client', header: 'Client', type: 'template', templateName: 'client' },
      { field: 'session', header: 'Subject' },
      { field: 'tutorName', header: 'Tutor' },
      { field: 'studentGroup', header: 'Learner', type: 'template', templateName: 'studentGroup' },
      { field: 'yearGroup', header: 'Key Stage' },
      { field: 'deliveryMode', header: 'Delivery', type: 'pill', width: '100px', pillMap: row => this.deliveryPill(row) },
      { field: 'startDate', header: 'Start Date', type: 'date', dateFormat: 'dd MMM yyyy', width: '110px' },
      { field: 'endDate', header: 'End Date', type: 'date', dateFormat: 'dd MMM yyyy', width: '110px' },
      { field: 'actions', header: 'Actions', type: 'template', templateName: 'actions', width: '110px' }
    ];
  }

  /** Online bookings expect a Digital Classroom, so they get the stand-out variant. */
  private deliveryPill(row: SearchPlacementResult): StatusVariant {
    return (row.deliveryMode || '').toLowerCase() === 'online' ? 'blue' : 'gray';
  }

  /** Builds the search request from the current filter criteria (paging is added separately). */
  private buildRequest(): SearchPlacementRequest {
    let request = new SearchPlacementRequest();
    request.bookingId = this.searchDataHelper.bookingId;
    request.clientName = this.searchDataHelper.clientNameOrId;
    request.searchTerm = this.searchDataHelper.searchTerm;
    request.showEnded = this.searchDataHelper.showEnded;
    request.clientIds = this.searchDataHelper.clientIds;
    // New parameters only when the switch is on; off, the request is the pre-filter shape. The
    // external client view has none of these filters, so it never restricts by them.
    if (this.filtersEnabled) {
      request.companyIds = this.externalView ? [] : (this.searchDataHelper.companyIds ?? []);
      if (!this.externalView) {
        request.deliveryMode = this.searchDataHelper.deliveryMode || null;
        request.provisionType = this.searchDataHelper.provisionType || null;
        request.startFrom = this.searchDataHelper.startFrom || null;
        request.startTo = this.searchDataHelper.startTo || null;
      }
    }
    return request;
  }

  /**
   * This screen always pages server-side, opting in via `PlacementService.searchPlacements`'s
   * second argument. The other `RunPlacementSearch` callers (`view-student-bookings`,
   * `session-overview` x2) call the service directly with no second argument and keep receiving
   * today's full, unpaged array - see the comment on `SearchPlacementRequest`.
   */
  private buildPaging(): PagedRequest {
    return {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortDirection: this.sortDir
    };
  }

  private assignData(response: PagedResponse<SearchPlacementResult>): void {
    this.searchDataHelper.placements = response && response.items ? response.items : new Array<SearchPlacementResult>();
    this.totalCount = response ? response.totalCount : 0;
    this.dotsFound = this.searchDataHelper.placements.findIndex(p => p.dotFlag > 0) != -1;
  }

  /** Runs a fresh search from page 1 - a new filter must not silently show a stale page's rows. */
  public searchPlacements(): void {
    this.pageNumber = 1;
    if (this.filtersEnabled && this.usesFilterUx()) {
      this.filterUx.save(this.filterState());
    }    this.searchRequested.next();
  }

  /** The client-facing view embeds this screen; only the internal screen remembers or shares filters. */
  private usesFilterUx(): boolean {
    return !this.externalView;
  }

  public onDatePreset(selection: DatePresetSelection): void {
    this.datePreset = selection.preset;
    this.searchDataHelper.startFrom = selection.from;
    this.searchDataHelper.startTo = selection.to;
  }

  /** Clear all: back to the defaults, forgotten, and out of the URL. */
  public clearAll(): void {
    this.searchDataHelper.clientNameOrId = null;
    this.searchDataHelper.bookingId = null;
    this.searchDataHelper.searchTerm = null;
    this.applyFilterState({});
    this.filterUx.clear();
    this.searchPlacements();
  }

  /** A preset is remembered as the preset, so it resolves afresh on the next visit. */
  private filterState(): FilterState {
    return {
      brand: this.searchDataHelper.companyIds,
      delivery: this.searchDataHelper.deliveryMode,
      orderType: this.searchDataHelper.provisionType,
      preset: this.datePreset,
      from: this.datePreset ? null : PlacementSearchComponent.isoDate(this.searchDataHelper.startFrom),
      to: this.datePreset ? null : PlacementSearchComponent.isoDate(this.searchDataHelper.startTo),
      ended: this.searchDataHelper.showEnded || null
    };
  }

  private applyFilterState(state: FilterState): void {
    this.searchDataHelper.companyIds = (state.brand as number[]) ?? [];
    this.searchDataHelper.deliveryMode = (state.delivery as string) ?? null;
    this.searchDataHelper.provisionType = (state.orderType as string) ?? null;
    this.searchDataHelper.showEnded = state.ended === true;
    this.datePreset = isDatePreset(state.preset) ? state.preset : null;
    const range = this.datePreset ? resolveDatePreset(this.datePreset) : null;
    // yyyy-MM-dd strings, the type the helper and the request carry.
    this.searchDataHelper.startFrom = range ? range.from : ((state.from as string) ?? null);
    this.searchDataHelper.startTo = range ? range.to : ((state.to as string) ?? null);
  }

  /** The date inputs hold a Date once edited; a restored or default value may be a yyyy-MM-dd string. */
  private static isoDate(value: any): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : toIsoDate(date);
  }

  /** A Brand filter change - kept on the shared helper with the other filters, applied on Search. */
  public brandsChanged(companyIds: number[]): void {
    this.searchDataHelper.companyIds = companyIds ?? [];
  }

  /** A page click or page-size change from the table - re-issues the search for the new page. */
  public onPageChange(event: PageChange): void {
    this.pageNumber = event.page;
    this.pageSize = event.pageSize;
    this.searchRequested.next();
  }

  /**
   * A column header sort. Only records the new sort state here - the table also emits `pageChange`
   * (reset to page 1) for server-side sorting, and that handler is what re-issues the request, so a
   * sort click results in exactly one request carrying both the sort and the page reset.
   */
  public onSortChange(event: SortChange): void {
    this.sortField = event.field;
    this.sortDir = event.direction;
  }

  public navigateItem(item: SearchPlacementResult, location: string): void {
    if (location == 'reports') {
      
      this.tutorReportsHelperService.setCommonDataBeforeNavigate(+item.bookingId, item.startDate, item.endDate);
      setTimeout(() => {
        this.router.navigate(["/tutorreports", +item.bookingId]);
      }, 200);
    } else if (location == 'setup') {
      this.placementService.getPlacementMaintenanceInfo(item.bookingId.toString()).subscribe((pm: PlacementMaintenance) => {
        if (pm != null && pm.placementInfo != null) {
          this.clientService.getClientUserDetailsByClientId(pm.placementInfo.clientId, item.bookingId).subscribe((clientUsers: ClientUserDetails[]) => {
            this.dialog.open(StudentGroupMaintenanceComponent, {
              data: { placementId: item.bookingId, externalView: this.externalView, placementMaintenance: pm, clientUserDetails: clientUsers },
              panelClass: 'group-maintenance-dialog'
            }).afterClosed().subscribe(changed => {
              if (changed) {
                this.searchPlacements();
              }
            });
          }, error => this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error));
        } else {
          this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", { isValidationError: false });
        }
      }, error => this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error));
    }
  }

}
